import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import { Pencil } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import cloudAssetService, {
  CloudResource,
  CloudResourceHistoryEntry,
  CLOUD_PROVIDER_STATE_LABELS,
} from '../../services/cloudAssetService';

interface Props {
  open: boolean;
  onClose: () => void;
  resource: CloudResource | null;
  onEdit: (resource: CloudResource) => void;
  lifecycleLabel: (value: string) => string;
  /** Provider display name for a stored code — the master lives on the page. */
  providerLabel: (code: string) => string;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="space-y-1">
    <div className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="text-sm">{children || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

const fullName = (person?: { firstName: string; lastName: string }) =>
  person ? `${person.firstName} ${person.lastName}` : '';

/**
 * Resource detail.
 *
 * Overview splits Resource Information from ITAM Information — with nothing
 * discovered, both halves were typed, but the split is what keeps business
 * context from reading as provider truth.
 */
export const CloudResourceDetailModal: React.FC<Props> = ({
  open,
  onClose,
  resource,
  onEdit,
  lifecycleLabel,
  providerLabel,
}) => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCost } = useCurrency();
  const [history, setHistory] = useState<CloudResourceHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!open || !resource) return;
    let cancelled = false;
    setLoadingHistory(true);
    cloudAssetService
      .getResourceHistory(resource.id)
      .then((entries) => {
        if (!cancelled) setHistory(entries);
      })
      .catch((error: any) => {
        if (!cancelled) {
          showToast(error?.friendlyMessage || 'Failed to load history', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
    // showToast is stable from context; re-running on it would refetch endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resource]);

  if (!resource) return null;

  const cost =
    resource.monthlyCost == null || resource.monthlyCost === ''
      ? null
      : Number(resource.monthlyCost);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl sm:p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {resource.name}
            <Badge variant="secondary">
              {providerLabel(resource.provider)}
            </Badge>
            <Badge variant="outline">
              {CLOUD_PROVIDER_STATE_LABELS[resource.providerState]}
            </Badge>
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {resource.providerResourceId}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
          <TabsList className="shrink-0 justify-start gap-6 rounded-none border-b bg-transparent px-6 py-0">
            {['overview', 'configuration', 'activity'].map((value) => (
              <TabsTrigger
                key={value}
                value={value}
                className="rounded-none border-none bg-transparent px-0 py-3 capitalize text-muted-foreground transition-all hover:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none"
              >
                {value}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent
            value="overview"
            className="mt-0 flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <section>
              <h3 className="mb-4 border-b pb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                Resource Information
              </h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Provider">
                  {providerLabel(resource.provider)}
                </Field>
                <Field label="Provider Resource ID">
                  <span className="font-mono text-xs">
                    {resource.providerResourceId}
                  </span>
                </Field>
                <Field label="Resource Type">
                  {resource.resourceType?.normalizedType}
                </Field>
                <Field label="Provider Resource Type">
                  {resource.providerResourceType}
                </Field>
                <Field label="Environment">{resource.environment?.name}</Field>
                <Field label="Region">{resource.region}</Field>
                <Field label="Availability Zone">
                  {resource.availabilityZone}
                </Field>
                <Field label="Provider State">
                  {CLOUD_PROVIDER_STATE_LABELS[resource.providerState]}
                </Field>
              </div>
            </section>

            <section>
              <h3 className="mb-4 border-b pb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                ITAM Information
              </h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Department">{resource.department?.name}</Field>
                <Field label="Lifecycle Status">
                  {lifecycleLabel(resource.lifecycleStatus)}
                </Field>
                <Field label="Business Owner">
                  {fullName(resource.businessOwner)}
                </Field>
                <Field label="Technical Owner">
                  {fullName(resource.technicalOwner)}
                </Field>
                <Field label="Requested By">
                  {fullName(resource.requestedBy)}
                </Field>
                <Field label="Approved By">
                  {fullName(resource.approvedBy)}
                </Field>
                <Field label="Environment Tier">{resource.environmentTier}</Field>
                <Field label="Application">{resource.application}</Field>
                <Field label="Cost Center">{resource.costCenter}</Field>
                {/* Who invoices this, which the provider badge above does not
                    answer whenever a reseller sits in between (Story 103). */}
                <Field label="Vendor">{resource.vendor?.name}</Field>
                <Field label="Monthly Cost">
                  {cost == null ? null : (
                    <span>
                      {formatCost(cost, resource.currency)}
                      {/* Manually entered — say when, never imply it is live. */}
                      {resource.costUpdatedAt && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          as entered{' '}
                          {new Date(resource.costUpdatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  )}
                </Field>
              </div>
            </section>
          </TabsContent>

          <TabsContent
            value="configuration"
            className="mt-0 flex-1 overflow-y-auto px-6 py-5"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Notes">{resource.notes}</Field>
              <Field label="Compliance Status">
                {resource.complianceStatus}
              </Field>
              <Field label="Created">
                {new Date(resource.createdAt).toLocaleString()}
              </Field>
              <Field label="Last Updated">
                {new Date(resource.updatedAt).toLocaleString()}
              </Field>
            </div>
          </TabsContent>

          <TabsContent
            value="activity"
            className="mt-0 flex-1 overflow-y-auto px-6 py-5"
          >
            {loadingHistory ? (
              <p className="text-sm text-muted-foreground">Loading history…</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No changes recorded yet.
              </p>
            ) : (
              <ul className="space-y-3">
                {history.map((entry) => (
                  <li
                    key={entry.id}
                    className="border-l-2 border-muted pl-3 text-sm"
                  >
                    <div className="font-medium">
                      {entry.field === 'record'
                        ? entry.newValue
                        : `${entry.field}: ${entry.oldValue ?? '—'} → ${entry.newValue ?? '—'}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fullName(entry.changedBy) || 'System'} ·{' '}
                      {new Date(entry.changedAt).toLocaleString()} · {entry.source}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mx-0 mt-0 shrink-0 gap-2 border-t bg-background px-6 py-3 sm:gap-0 sm:border-t sm:bg-background sm:px-6 sm:py-3">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {hasPermission('cloud.edit') && (
            <Button onClick={() => onEdit(resource)} className="min-w-[140px]">
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloudResourceDetailModal;
